/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

import React from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';

const ManifestoPage = () => {
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-brand-bgOff via-white to-white">

      <PublicNav />

      {/* ─── Decorative radial glows ─────────────────────────────── */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -right-32"
        style={{
          width: '500px',
          height: '500px',
          background:
            'radial-gradient(circle, rgba(92, 225, 230, 0.15) 0%, transparent 70%)'
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-24 -left-24"
        style={{
          width: '400px',
          height: '400px',
          background:
            'radial-gradient(circle, rgba(33, 150, 243, 0.08) 0%, transparent 70%)'
        }}
      />

      <div className="relative max-w-3xl mx-auto px-6 pt-16 sm:pt-20 pb-16">

        {/* ── Back link ── */}
        <div className="mt-6">
          <Link
            to="/welcome"
            className="text-sm text-brand-cyan hover:text-brand-cyanDark font-medium hover:underline"
          >
            ← Back to home
          </Link>
        </div>

        {/* ── Hero ── */}
        <header className="text-center mt-10 sm:mt-12">
          <p className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
            Why SkillDelivery exists
          </p>
          <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tighter leading-tight bg-gradient-to-br from-brand-cyan to-brand-blue bg-clip-text text-transparent">
            SkillDelivery is built on a simple idea!
          </h1>
        </header>

        {/* ── Manifesto body ── */}
        <article className="mt-12 sm:mt-14 space-y-6 text-base sm:text-lg text-brand-text leading-relaxed">

          <p>
            The world is full of talented practitioners — and the teams,
            universities, and institutes that shape them — whose work is invisible.
          </p>

          <p>You've solved problems no one else on your team can replicate.</p>
          <p>You've shipped systems that took years to truly understand.</p>
          <p>
            You hold knowledge that no university teaches — earned through
            nights, weekends, near-misses, and one really good mentor.
          </p>

          <p className="text-brand-muted italic">
            And the world doesn't see any of it.
          </p>

          <p className="text-xl sm:text-2xl font-bold text-brand-text pt-2">
            SkillDelivery is your stage.
          </p>

          <p>We start with one thing: showing you who you already are.</p>

          <p>
            Every question you ask. Every problem you solve. Every domain
            you master. We capture it, organize it, and reflect it back —
            so for the first time, you can <span className="font-semibold text-brand-cyan">SEE</span> what you've actually built.
          </p>

          <p className="text-lg sm:text-xl font-semibold text-brand-text2 pt-2">
            Then we go further.
          </p>

          <p>
            Your knowledge becomes visible to other practitioners who share
            your domain. Mentors find their mentees. Capabilities meet
            opportunities. Worth meets recognition.
          </p>

          <p className="font-semibold text-brand-text pt-2">
            This is the platform where expertise becomes visible:
          </p>

          <ul className="space-y-2 pl-2">
            <li className="flex items-start gap-3">
              <span className="text-brand-cyan font-bold mt-0.5" aria-hidden="true">—</span>
              <span><span className="font-semibold text-brand-text">Individuals</span> to themselves, then to peers.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-brand-cyan font-bold mt-0.5" aria-hidden="true">—</span>
              <span><span className="font-semibold text-brand-text">Organizations</span> to clients and partners.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-brand-cyan font-bold mt-0.5" aria-hidden="true">—</span>
              <span><span className="font-semibold text-brand-text">Universities</span> to industry and graduates.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-brand-cyan font-bold mt-0.5" aria-hidden="true">—</span>
              <span><span className="font-semibold text-brand-text">Institutes</span> to the practitioners they shape.</span>
            </li>
          </ul>

          <p className="pt-2">This isn't a learning platform.</p>

          <p>
            This is the platform where <span className="font-bold text-brand-text">YOU</span> become visible — to yourself first,
            and to the world that needed to know you all along.
          </p>

          <p className="text-2xl sm:text-3xl font-extrabold tracking-tight bg-gradient-to-br from-brand-cyan to-brand-blue bg-clip-text text-transparent pt-4">
            Welcome home!
          </p>
        </article>

        {/* ── Footer CTAs ── */}
        <section className="mt-16 sm:mt-20 text-center border-t border-brand-border pt-12">
          <p className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
            Ready to become visible?
          </p>
          <h2 className="mt-4 text-2xl sm:text-3xl font-bold text-brand-text tracking-tight">
            Pick the path that fits where you are.
          </h2>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center">
            <Link
              to="/welcome"
              className="inline-flex items-center justify-center px-6 py-3 rounded-brand bg-gradient-to-br from-brand-cyanLight to-brand-blue text-white font-semibold text-sm shadow-brandButton hover:shadow-brandButtonHover hover:-translate-y-px transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2"
            >
              Choose your path →
            </Link>
            <Link
              to="/tour"
              className="inline-flex items-center justify-center px-6 py-3 rounded-brand border-2 border-brand-cyan text-brand-cyan font-semibold text-sm bg-white hover:bg-brand-bgOff2 hover:-translate-y-px transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2"
            >
              Take the tour
            </Link>
          </div>
        </section>

      </div>

      <PublicFooter />
    </div>
  );
};

export default ManifestoPage;
